import { Avatar, Card, CardContent, CardHeader, Divider, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import clsx from "clsx";
import { EmptyState, LoadingIndicator, RenderGuard, ErrorMessage } from "components";
import PropTypes from "prop-types";
import React from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import PerfectScrollbar from "react-perfect-scrollbar";
import useErrorCatcher from "utils/useErrorCatcher";
import { useState } from "react";
import capitalise from "utils/capitalise";
import { Mastery } from "services";

const grid = 8;

const getItemStyle = (isDragging, draggableStyle) => ({
  // some basic styles to make the items look a bit nicer
  userSelect: "none",
  padding: grid * 2,
  margin: `0 0 0 0`,
  display: "flex",
  flexDirection: "horizontal",

  // change background colour if dragging
  background: isDragging ? "lightgrey" : "white",

  // styles we need to apply on draggables
  ...draggableStyle
});

const getListStyle = () => ({
  background: "white",
  padding: 0,
  width: "100%"
});

const useStyles = makeStyles(theme => ({
  root: {},
  content: {
    padding: 0,
    paddingBottom: "0 !important"
  },
  nameCell: {
    display: "flex",
    alignItems: "center"
  },
  actions: {
    padding: theme.spacing(1),
    justifyContent: "flex-end"
  },
  mono: {
    fontFamily: "Roboto Mono"
  },
  avatar: {
    height: 40,
    width: 40,
    marginRight: theme.spacing(2)
  },
  avatarbox: {
    display: "flex",
    flexDirection: "row"
  },
  inner: {
    minWidth: 700
  },
  number: {
    verticalAlign: "middle",
    fontSize: "2rem",
    fontWeight: "700",
    lineHeight: 1.2,
    marginRight: theme.spacing(2)
  }
}));

const Results = props => {
  const { models, loading, setModels } = props;

  const classes = useStyles();
  const [errorMessage, setErrorMessage] = useState(null);
  const runAsyncBlock = useErrorCatcher((error) => {
    setErrorMessage(error && capitalise(error.message));
    error && setModels(models);
  });

  const onDragEnd = result => {
    if (!result.destination) return;

    const output = Array.from(models);
    const [removed] = output.splice(result.source.index, 1);
    output.splice(result.destination.index, 0, removed);
    setModels(output);
    handleSubmit(output);
  }

  const handleSubmit = (output) => {
    runAsyncBlock(async () => {
      await Mastery.reorder({ order: output.map(model => model.id) });
    });
  }

  return (
    <Card>
      <CardHeader title="Mastery Order" />
      <Divider />
      <LoadingIndicator className={classes.spinner} variant="card" active={loading} />
      <RenderGuard renderIf={errorMessage}>
        <CardContent>
          <ErrorMessage message={errorMessage} />
        </CardContent>
      </RenderGuard>
      <RenderGuard renderIf={!loading}>
        <CardContent className={classes.content} style={{ paddingBottom: null }}>
          <PerfectScrollbar>
            <div className={classes.inner}>
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable style={{ width: "100%" }} droppableId="mastery_reorder">
                  {(provided, snapshot) => (
                    <div {...provided.droppableProps} ref={provided.innerRef} style={getListStyle(snapshot.isDraggingOver)}>
                      {models.map((model, index) => (
                        <Draggable key={`${model.id}`} draggableId={`${model.id}`} index={index}>
                          {(provided, snapshot) => (
                            <div>
                              <div ref={provided.innerRef} {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}>
                                <span className={classes.number}>{index + 1}</span>
                                <div className={clsx(classes.header, classes.avatarbox)}>
                                  <Avatar
                                    className={classes.avatar}
                                    src={model.main_image ? model.main_image.uri : "/images/avatars/placeholder_image.png"}
                                  />
                                  <div>
                                    <div>{model.name_en}{model.name_zh ? " " + model.name_zh : ""}</div>
                                    <div><Typography variant="caption">{model.code}</Typography></div>
                                  </div>
                                </div>
                              </div>
                              <Divider></Divider>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>
          </PerfectScrollbar>
        </CardContent>
        <EmptyState active={!models.length} />
      </RenderGuard>
    </Card>
  )
};

Results.propTypes = {
  className: PropTypes.string,
  loading: PropTypes.bool,
  onPageChange: PropTypes.func.isRequired,
  filter: PropTypes.object.isRequired,
  models: PropTypes.array.isRequired,
  setModels: PropTypes.func.isRequired,
};

Results.defaultProps = {
  loading: false,
  models: [],
  setModels: () => { },
  filter: {}
};

export default Results;